import React, { useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Button, Space, Typography, theme, Modal, Tooltip, Card, Row, Col, message, Select } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faStethoscope,
  faHeart,
  faLungs,
  faUser,
  faHandPaper,
  faPlus,
  faTimes,
  faFileAlt,
  faClipboardCheck,
  faEye,
  faCircle,
  faBrain,
  faBone,
  faClipboardList
} from '@fortawesome/free-solid-svg-icons'

import { allDataPoints, getCategories } from './data'
import TemplateBuilder from './components/TemplateBuilder'
import TemplateForm from './components/TemplateForm'
import { hexToRGBA } from '../../../../../lib/utils'
import {
  createAssessmentTemplate,
  readAssessmentTemplates,
  assessmentTemplateRecordCreate,
  assessmentTemplateRecordsRead,
  assessmentTemplateRecordUpdate
} from './utils/api'
import { useSelector } from 'react-redux'
import { handleError } from '../../../../../lib/errorHandler'

const { Title, Text } = Typography
const { Option } = Select

// Category icons mapping
const categoryIcons = {
  General: faFileAlt,
  Cardiac: faHeart,
  'General Appearance': faUser,
  Skin: faHandPaper,
  Respiratory: faLungs,
  Observations: faClipboardCheck,
  HEENT: faEye,
  Abdomen: faCircle,
  Neurological: faBrain,
  Orthopaedic: faBone,
  Diagnosis: faClipboardList
}

const ClinicalTemplates = ({ bookingData, userRef, onTemplateGenerate, onTemplateInsert }) => {
  const { token } = theme.useToken()
  const [showTemplateBuilder, setShowTemplateBuilder] = useState(false)
  const [activeTemplate, setActiveTemplate] = useState(null) // Single active template
  const [templates, setTemplates] = useState([])
  const [templateRecord, setTemplateRecord] = useState(null) // Current template record
  const globalState = useSelector((state) => state)

  console.log('templates', templates)

  useEffect(() => {
    fetchTemplates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchTemplates = async () => {
    const filter = {}
    try {
      filter.$or = [{ userRef: globalState.auth.agiliteUser._id }, { bookingRef: bookingData._id }]
      const response = await readAssessmentTemplates(filter)
      setTemplates(response)
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  const fetchTemplateRecord = async (templateId) => {
    try {
      const filter = {
        templateRef: templateId,
        bookingRef: bookingData._id,
        isActive: true
      }
      const response = await assessmentTemplateRecordsRead(filter)
      return response && response.length > 0 ? response[0] : null
    } catch (e) {
      console.error('Error fetching template record:', e)
      return null
    }
  }

  const saveTemplateRecord = async (templateData, values, status = 'completed') => {
    try {
      const recordData = {
        templateRef: templateData._id || templateData.id,
        bookingRef: bookingData._id,
        templateName: templateData.name,
        categories: templateData.categories || [],
        values: values,
        content: templateData.content || '',
        status: status,
        completedAt: status === 'completed' ? new Date() : null
      }

      let response
      if (templateRecord && templateRecord._id) {
        // Update existing record
        response = await assessmentTemplateRecordUpdate(templateRecord._id, recordData)
      } else {
        // Create new record
        response = await assessmentTemplateRecordCreate(recordData)
      }
      setTemplateRecord(response)
      return response
    } catch (e) {
      message.error('Failed to save template record: ' + handleError(e, true))
      throw e
    }
  }

  const handleTemplateCreate = async (template) => {
    console.log('template', template)
    try {
      const response = await createAssessmentTemplate(template)
      console.log('response', response)
    } catch (e) {
      console.log('error', e)
    }
    // Set the new template as the active template
    setActiveTemplate({ ...template, id: Date.now() })
    setShowTemplateBuilder(false)
  }

  const handleTemplateComplete = async (templateId, completedData) => {
    try {
      // Save the completed template as a record
      await saveTemplateRecord(activeTemplate, completedData.values, 'completed')

      // Insert into clinical notes
      console.log('completedData', completedData)
      if (onTemplateInsert) {
        onTemplateInsert(completedData)
      }

      message.success('Template completed and saved successfully')

      // Don't remove the template - let it stay for future reference and editing
      // The template form itself will handle the visual state changes
    } catch (e) {
      console.error('Error completing template:', e)
    }
  }

  const handleRemoveTemplate = () => {
    setActiveTemplate(null)
    setTemplateRecord(null)
  }

  const handleTemplateDraftSave = async (templateData, values) => {
    try {
      await saveTemplateRecord(activeTemplate, values, 'draft')
      message.success('Draft saved')
    } catch (e) {
      console.error('Error saving draft:', e)
      message.error('Failed to save draft')
    }
  }

  const handleTemplateSelect = async (templateId) => {
    const selectedTemplate = templates.find((template) => template._id === templateId)

    if (selectedTemplate) {
      // Check if the same template is already active
      if (
        activeTemplate &&
        (activeTemplate._id === selectedTemplate._id || activeTemplate.id === selectedTemplate._id)
      ) {
        message.warning('This template is already active')
        return
      }

      try {
        // Fetch existing record for this template and booking
        const existingRecord = await fetchTemplateRecord(templateId)
        setTemplateRecord(existingRecord)

        // Set the selected template as the active template
        const templateToAdd = {
          ...selectedTemplate,
          id: selectedTemplate._id || Date.now(), // Use _id if available, otherwise use timestamp
          // If there's an existing record, merge its values with the template's default values
          values: existingRecord ? existingRecord.values : selectedTemplate.values || {}
        }
        setActiveTemplate(templateToAdd)

        if (existingRecord) {
          message.info(`Loaded existing ${existingRecord.status} template record`)
        }
      } catch (e) {
        console.error('Error loading template:', e)
        message.error('Failed to load template')
      }
    }
  }

  return (
    <>
      {/* Clinical Templates Card Container */}
      <Card
        title={
          <Space>
            <FontAwesomeIcon icon={faStethoscope} style={{ color: token.colorPrimary }} />
            <span>Clinical Templates</span>
            {activeTemplate && (
              <Text type='secondary' style={{ fontSize: '12px', fontWeight: 'normal' }}>
                ({templateRecord ? `${templateRecord.status}` : 'new'})
              </Text>
            )}
          </Space>
        }
        extra={
          <Space>
            <Tooltip title='Select from existing clinical templates'>
              <Select
                placeholder='Select existing template'
                style={{ minWidth: '200px' }}
                onChange={handleTemplateSelect}
                allowClear
                showSearch
                filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
              >
                {templates.map((template) => (
                  <Option key={template._id} value={template._id}>
                    {template.name}
                  </Option>
                ))}
              </Select>
            </Tooltip>

            <Tooltip title='Create a structured clinical template with multiple examination categories'>
              <Button
                type='dashed'
                icon={<FontAwesomeIcon icon={faPlus} />}
                onClick={() => setShowTemplateBuilder(true)}
                style={{
                  borderColor: token.colorPrimary,
                  color: token.colorPrimary
                }}
              >
                Add Clinical Template
              </Button>
            </Tooltip>
          </Space>
        }
        style={{
          marginBottom: '16px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}
        styles={{
          header: {
            borderBottom: `1px solid ${token.colorBorder}`,
            backgroundColor: token.colorFillAlter
          },
          body: {
            padding: activeTemplate ? '16px' : '0'
          }
        }}
      >
        {/* Active Template Form */}
        {activeTemplate ? (
          <TemplateForm
            key={activeTemplate.id}
            template={activeTemplate}
            onComplete={(completedData) => handleTemplateComplete(activeTemplate.id, completedData)}
            onRemove={handleRemoveTemplate}
            onDraftSave={handleTemplateDraftSave}
            existingRecord={templateRecord}
          />
        ) : (
          <div
            style={{
              textAlign: 'center',
              padding: '24px',
              color: token.colorTextSecondary
            }}
          >
            <FontAwesomeIcon
              icon={faStethoscope}
              style={{
                fontSize: '24px',
                marginBottom: '8px',
                color: token.colorTextDisabled
              }}
            />
            <div>Select a template or create a new one to get started</div>
          </div>
        )}
      </Card>

      {/* Template Builder Modal */}
      <Modal
        title={
          <Space>
            <FontAwesomeIcon icon={faStethoscope} style={{ color: token.colorPrimary }} />
            <span>Create Clinical Template</span>
          </Space>
        }
        open={showTemplateBuilder}
        onCancel={() => setShowTemplateBuilder(false)}
        footer={null}
        width='100vw'
        style={{
          top: 0,
          paddingBottom: 0,
          maxWidth: 'none'
        }}
        styles={{
          body: {
            height: 'calc(100vh - 55px)',
            padding: '16px',
            overflow: 'auto',
            backgroundColor: '#f5f5f5'
          },
          mask: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)'
          }
        }}
        destroyOnClose
        maskClosable={false}
        zIndex={1000}
      >
        <TemplateBuilder
          onTemplateCreate={handleTemplateCreate}
          bookingData={bookingData}
          userRef={userRef}
          isModal={true}
        />
      </Modal>
    </>
  )
}

ClinicalTemplates.propTypes = {
  bookingData: PropTypes.object,
  userRef: PropTypes.object,
  onTemplateGenerate: PropTypes.func,
  onTemplateInsert: PropTypes.func
}

export default ClinicalTemplates
